import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import StudentHeader from '../components/StudentHeader';
import { studentFetch } from '../utils/authFetch';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SUBJECT_META = {
  'Mathematics':              { icon:'🔢', ac:'#7c3aed', ibg:'#ede9fe', sh:'rgba(124,58,237,.22)',  tilt:'rotate(-1.5deg)' },
  'Science':                  { icon:'🔬', ac:'#0891b2', ibg:'#cffafe', sh:'rgba(8,145,178,.2)',    tilt:'rotate(1deg)'    },
  'English':                  { icon:'📖', ac:'#d97706', ibg:'#fef3c7', sh:'rgba(217,119,6,.2)',    tilt:'rotate(-0.8deg)' },
  'Computing':                { icon:'💻', ac:'#059669', ibg:'#d1fae5', sh:'rgba(5,150,105,.2)',    tilt:'rotate(1.2deg)'  },
  'Computer Science':         { icon:'💻', ac:'#059669', ibg:'#d1fae5', sh:'rgba(5,150,105,.2)',    tilt:'rotate(1.2deg)'  },
  'ICT':                      { icon:'💻', ac:'#059669', ibg:'#d1fae5', sh:'rgba(5,150,105,.2)',    tilt:'rotate(1.2deg)'  },
  'Global Perspectives':      { icon:'🌍', ac:'#0ea5e9', ibg:'#e0f2fe', sh:'rgba(14,165,233,.2)',   tilt:'rotate(-1deg)'   },
  'Modern Foreign Language':  { icon:'🗣️', ac:'#ec4899', ibg:'#fce7f3', sh:'rgba(236,72,153,.2)',   tilt:'rotate(0.8deg)'  },
  'التربية الإسلامية':        { icon:'☪️',  ac:'#059669', ibg:'#d1fae5', sh:'rgba(5,150,105,.2)',    tilt:'rotate(-1.2deg)' },
  'اللغة العربية':            { icon:'✍️',  ac:'#b45309', ibg:'#fef3c7', sh:'rgba(180,83,9,.2)',     tilt:'rotate(1.5deg)'  },
  'تاريخ قطر والمواطنة':      { icon:'🇶🇦', ac:'#dc2626', ibg:'#fee2e2', sh:'rgba(220,38,38,.2)',    tilt:'rotate(-1deg)'   },
  'Physics':                  { icon:'⚛️',  ac:'#6d28d9', ibg:'#ede9fe', sh:'rgba(109,40,217,.2)',   tilt:'rotate(1deg)'    },
  'Chemistry':                { icon:'🧪', ac:'#16a34a', ibg:'#dcfce7', sh:'rgba(22,163,74,.2)',    tilt:'rotate(-1deg)'   },
  'Biology':                  { icon:'🧬', ac:'#0891b2', ibg:'#cffafe', sh:'rgba(8,145,178,.2)',    tilt:'rotate(1.2deg)'  },
  'History':                  { icon:'📜', ac:'#b45309', ibg:'#fef3c7', sh:'rgba(180,83,9,.2)',     tilt:'rotate(-0.8deg)' },
  'Geography':                { icon:'🌍', ac:'#0ea5e9', ibg:'#e0f2fe', sh:'rgba(14,165,233,.2)',   tilt:'rotate(1deg)'    },
};
const DEFAULT_META = { icon:'📚', ac:'#059669', ibg:'#d1fae5', sh:'rgba(5,150,105,.2)', tilt:'rotate(0deg)' };

const T = {
  ar: {
    title:            'اختر موضوعك',
    back:             'رجوع',
    loading:          'جارٍ التحميل…',
    error_load:       'تعذّر تحميل المواد. حاول مرة أخرى.',
    retry:            'إعادة المحاولة',
    no_subjects:      'لا توجد مواد متاحة لصفّك حتى الآن.',
    no_units:         'لا توجد وحدات لهذه المادة.',
    no_topics_yet:    'لا توجد مواضيع مدرجة بعد',
    other_unit:       '✏️ وحدة غير مدرجة...',
    other_topic:      '✏️ موضوع غير مدرج...',
    custom_subtitle:  'أضف وحدة أو موضوع غير مدرج في المنهج',
    custom_unit:      'اسم الوحدة',
    custom_unit_ph:   'اكتب اسم الوحدة…',
    custom_topic:     'اسم الموضوع',
    custom_ph:        'اكتب الموضوع الذي تريد مراجعته…',
    custom_cancel:    'إلغاء',
    start:            'ابدأ الجلسة',
    starting:         'جارٍ التحضير…',
    incomplete_title: 'لديك جلسة غير مكتملة',
    incomplete_in:    'في',
    incomplete_cont:  'متابعة الجلسة',
    incomplete_ign:   'تجاهل',
    studied:          'تعلّمتها من قبل',
    continue_badge:   'متابعة',
  },
  en: {
    title:            'Choose Your Topic',
    back:             'Back',
    loading:          'Loading…',
    error_load:       'Failed to load subjects. Please try again.',
    retry:            'Retry',
    no_subjects:      'No subjects available for your grade yet.',
    no_units:         'No units found for this subject.',
    no_topics_yet:    'No topics listed yet',
    other_unit:       '✏️ Unlisted unit...',
    other_topic:      '✏️ Unlisted topic...',
    custom_subtitle:  'Add an unlisted unit or topic',
    custom_unit:      'Unit name',
    custom_unit_ph:   'Enter unit name…',
    custom_topic:     'Topic name',
    custom_ph:        'Type the topic you want to study…',
    custom_cancel:    'Cancel',
    start:            'Start Session',
    starting:         'Preparing…',
    incomplete_title: 'You have an incomplete session',
    incomplete_in:    'in',
    incomplete_cont:  'Continue Session',
    incomplete_ign:   'Dismiss',
    studied:          'Already studied',
    continue_badge:   'Continue',
  },
};

export default function SubjectSelector() {
  const navigate = useNavigate();

  const { lang }   = useLanguage();
  const t          = T[lang] || T.ar;
  const dir        = lang === 'ar' ? 'rtl' : 'ltr';
  const studentRaw = localStorage.getItem('student_data');
  const student    = studentRaw ? JSON.parse(studentRaw) : null;
  const token      = localStorage.getItem('student_token');

  const [loading,            setLoading]           = useState(true);
  const [error,              setError]             = useState(null);
  const [subjects,           setSubjects]          = useState([]);
  const [incompleteSessions, setIncompleteSessions] = useState([]);
  const [isMobile, setIsMobile] = useState(false);

  const [sel, setSel] = useState({
    subjectId:   null,
    subjectName: null,
    subjectAr:   null,
    unitId:      null,
    unitName:    null,
    topicId:     null,
    topicName:   null,
  });
  const [customMode,       setCustomMode]       = useState(false);
  const [customUnit,       setCustomUnit]       = useState('');
  const [customUnitLocked, setCustomUnitLocked] = useState(false);
  const [customTopic,      setCustomTopic]      = useState('');
  const [starting,         setStarting]         = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const loadData = useCallback(async () => {
    if (!student || !token) {
      navigate('/student/auth', { replace: true });
      return;
    }
    const SUBJ_CACHE_KEY = `jahbath_subjects_${student.id}`;
    const SUBJ_CACHE_TTL = 5 * 60 * 1000;
    const cachedSubj = localStorage.getItem(SUBJ_CACHE_KEY);
    const cachedSubjTime = localStorage.getItem(SUBJ_CACHE_KEY + '_time');
    if (cachedSubj && cachedSubjTime && Date.now() - Number(cachedSubjTime) < SUBJ_CACHE_TTL) {
      setSubjects(JSON.parse(cachedSubj));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [subjRes, sessRes] = await Promise.all([
        studentFetch(`${API}/student/subjects/${student.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        studentFetch(`${API}/student/incomplete-sessions/${student.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (!subjRes || !sessRes) return;
      const subjData = await subjRes.json();
      const sessData = await sessRes.json();
      if (subjData.success) {
        setSubjects(subjData.subjects || []);
        localStorage.setItem(SUBJ_CACHE_KEY, JSON.stringify(subjData.subjects || []));
        localStorage.setItem(SUBJ_CACHE_KEY + '_time', Date.now().toString());
      } else {
        setError(t.error_load);
      }
      if (sessData.success) setIncompleteSessions(sessData.sessions || []);
    } catch {
      setError(t.error_load);
    } finally {
      setLoading(false);
    }
  }, [student?.id, token]); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  const visibleIncomplete = incompleteSessions.find(
    s => s.subject && s.unit
  ) || null;


  function selectTopic(subj, unit, topic) {
    setSel({
      subjectId:   subj.subject_id,
      subjectName: subj.subject_name,
      subjectAr:   subj.subject_name_ar,
      unitId:      unit.unit_id,
      unitName:    unit.unit_name,
      topicId:     topic.topic_id,
      topicName:   topic.topic_name_ar || topic.topic_name,
    });
    setCustomMode(false);
    setCustomUnit('');
    setCustomUnitLocked(false);
    setCustomTopic('');
  }

  function openCustomFromCard(e, subj) {
    e.stopPropagation();
    setSel(s => ({
      ...s,
      subjectId:   subj.subject_id,
      subjectName: subj.subject_name,
      subjectAr:   subj.subject_name_ar,
      unitId:      null,
      unitName:    null,
      topicId:     null,
      topicName:   null,
    }));
    setCustomMode(true);
    setCustomUnit('');
    setCustomUnitLocked(false);
    setCustomTopic('');
  }

  function openCustomFromTopics(e, subj, unit) {
    e.stopPropagation();
    setSel(s => ({
      ...s,
      subjectId:   subj.subject_id,
      subjectName: subj.subject_name,
      subjectAr:   subj.subject_name_ar,
      unitId:      unit.unit_id,
      unitName:    unit.unit_name,
      topicId:     null,
      topicName:   null,
    }));
    setCustomMode(true);
    setCustomUnit(unit.unit_name_ar || unit.unit_name);
    setCustomUnitLocked(true);
    setCustomTopic('');
  }

  function closeCustom() {
    setCustomMode(false);
    setCustomUnit('');
    setCustomUnitLocked(false);
    setCustomTopic('');
  }

  const canStart = !starting && sel.subjectId && (
    customMode
      ? customUnit.trim().length > 0 && customTopic.trim().length > 0
      : sel.unitId && sel.topicId !== null
  );

  const selSubject = subjects.find(s => s.subject_id === sel.subjectId);
  const startEmoji = selSubject?.subject_emoji || '📖';
  const startSubjectLabel = lang === 'ar'
    ? (selSubject?.subject_name_ar || sel.subjectName || '')
    : (sel.subjectName || '');
  const startUnitLabel  = customMode ? customUnit.trim()  : (sel.unitName  || '');
  const startTopicLabel = customMode ? customTopic.trim() : (sel.topicName || '');

  async function handleStart() {
    if (!canStart) return;
    setStarting(true);

    const unitName  = customMode ? customUnit.trim()  : sel.unitName;
    const topicName = customMode ? customTopic.trim() : sel.topicName;

    if (customMode) {
      studentFetch(`${API}/student/notify-unlisted-topic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          student_id: student.id,
          subject:    sel.subjectName,
          unit:       unitName,
          topic:      topicName,
        }),
      }).catch(() => {});
    }

    let learningObjectives = [];
    let topicList = [];

    if (!customMode) {
      try {
        const sliceRes = await studentFetch(`${API}/registry/slice`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            student_id:        student.id,
            requesting_agent:  'explainer',
            subject:           sel.subjectName,
            unit:              unitName,
            grade:             student.grade,
            student_profile:   {},
          }),
        });
        if (!sliceRes) return;
        const sliceData = sliceRes.ok ? await sliceRes.json() : {};
        learningObjectives = sliceData?.payload?.learning_objectives || sliceData?.learning_objectives || sliceData?.objectives || [];
        topicList = sliceData?.payload?.topic_list || sliceData?.topic_list || [];
      } catch {
        // silent — ExplanationScreen fallback fetches slice itself
      }
    }

    navigate('/student/explanation', {
      state: {
        student_id:          student.id,
        subject_id:          sel.subjectId,
        subject:             sel.subjectName,
        unit_id:             sel.unitId,
        unit:                unitName,
        topic:               topicName,
        is_custom:           customMode,
        grade:               student.grade,
        learning_objectives: learningObjectives,
        topic_list:          topicList,
        delivery_language:   selSubject?.delivery_language || 'ar',
      },
    });
  }

  function handleContinueSession(session) {
    navigate('/student/explanation', {
      state: {
        student_id: student.id,
        session_id: session.id,
        subject:    session.subject,
        unit:       session.unit,
        topic:      session.unit,
        resume:     true,
      },
    });
  }

  function handleNavigateToTopic(subj, unit, topic) {
    const conceptIndex = unit.topics.findIndex(t => t.topic_id === topic.topic_id);
    navigate('/student/explanation', {
      state: {
        student_id:    student.id,
        subject_id:    subj.subject_id,
        subject:       subj.subject_name,
        unit_id:       unit.unit_id,
        unit:          unit.unit_name,
        topic:         topic.topic_name,
        topic_list:    unit.topics.map(t => t.topic_name),
        concept_index: conceptIndex >= 0 ? conceptIndex : 0,
        is_custom:     false,
      },
    });
  }

  return (
    <div className="sbs-root" dir={dir}>
      <canvas ref={canvasRef} style={{ position:'fixed', inset:0, zIndex:0, pointerEvents:'none' }} />
      <style>{CSS}</style>

      <StudentHeader
        title={lang === 'ar' ? 'اختر موضوعك' : 'Choose Your Topic'}
        showBack
        onBack={() => navigate('/student/dashboard')}
        lang={lang}
      />

      <main className="sbs-main">

        {visibleIncomplete && (
          <div className="sbs-banner">
            <div className="sbs-banner-text">
              <span className="sbs-banner-icon">⏸️</span>
              <span>
                {t.incomplete_title} — {t.incomplete_in}{' '}
                <strong>{visibleIncomplete.subject}</strong>{' '}
                / {visibleIncomplete.unit}
              </span>
            </div>
            <div className="sbs-banner-actions">
              <button
                className="sbs-banner-cont"
                onClick={() => handleContinueSession(visibleIncomplete)}
              >
                {t.incomplete_cont}
              </button>
            </div>
          </div>
        )}

        {loading && (
          <div className="sbs-center">
            <div className="sbs-spinner" />
            <p className="sbs-hint">{t.loading}</p>
          </div>
        )}

        {!loading && error && (
          <div className="sbs-center">
            <p className="sbs-error">{error}</p>
            <button className="sbs-retry-btn" onClick={loadData}>{t.retry}</button>
          </div>
        )}

        {!loading && !error && subjects.length === 0 && (
          <p className="sbs-empty">{t.no_subjects}</p>
        )}

        {!loading && !error && subjects.length > 0 && (
          <div className="sbs-subj-grid" style={{
            display:'grid',
            gridTemplateColumns:'repeat(4, 1fr)',
            gap: 16,
            padding:'0 8px',
          }}>
            {subjects.map((subj) => {
              const meta = SUBJECT_META[subj.subject_name] || DEFAULT_META;

              return (
                <div
                  key={subj.subject_name}
                  style={{ position:'relative', cursor:'pointer' }}
                  onClick={() => navigate('/student/subject-units', { state: { subj } })}
                >
                  {/* CARD */}
                  <div
                    style={{
                      display:'flex', flexDirection:'column', alignItems:'center',
                      gap:'10px',
                      padding:'24px 16px 20px',
                      borderRadius: 24,
                      border: '2px solid #E5E7EB',
                      background:'white',
                      transform: meta.tilt,
                      boxShadow: '0 5px 0 #E5E7EB, 0 10px 28px rgba(0,0,0,.07)',
                      transition:'transform .15s, box-shadow .15s',
                      position:'relative', zIndex: 1,
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-6px)';
                      e.currentTarget.style.boxShadow = '0 5px 0 #D1D5DB, 0 18px 36px rgba(0,0,0,.1)';
                      e.currentTarget.style.borderColor = '#D1D5DB';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = meta.tilt;
                      e.currentTarget.style.boxShadow = '0 5px 0 #E5E7EB, 0 10px 28px rgba(0,0,0,.07)';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                    }}
                  >
                    <div style={{
                      width: 52, height: 52, borderRadius: 18,
                      background: meta.ibg,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:'26px',
                      transform: 'translateY(-8px)',
                      filter: 'drop-shadow(0 6px 10px rgba(0,0,0,.18))',
                      transition:'transform .15s, filter .15s',
                    }}>
                      {meta.icon}
                    </div>
                    <div style={{
                      fontSize:'13px', fontWeight:700, color:'#1e3a5f',
                      textAlign:'center', lineHeight:1.3,
                    }}>
                      {subj.subject_name}
                    </div>
                    <div style={{
                      width: '28px',
                      height:'3px', borderRadius:'2px',
                      background: meta.ac,
                      opacity: 0.3,
                      marginTop:'2px',
                      transition:'all .3s',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {customMode && sel.subjectId && (
          <div className="sbs-custom-full">
            <button className="sbs-custom-close" onClick={closeCustom}>✕</button>
            <div className="sbs-custom-full-header">
              <span className="sbs-custom-full-emoji">
                {selSubject?.subject_emoji || '📖'}
              </span>
              <div className="sbs-custom-full-header-text">
                <span className="sbs-custom-full-subject">
                  {lang === 'ar' ? selSubject?.subject_name_ar : selSubject?.subject_name}
                </span>
                <span className="sbs-custom-subtitle">{t.custom_subtitle}</span>
              </div>
            </div>
            <div className="sbs-custom-full-fields">
              <div className="sbs-custom-field">
                <label className="sbs-custom-label">{t.custom_unit}</label>
                <input
                  className={`sbs-custom-input${customUnitLocked ? ' sbs-custom-input-locked' : ''}`}
                  placeholder={t.custom_unit_ph}
                  value={customUnit}
                  readOnly={customUnitLocked}
                  onChange={e => !customUnitLocked && setCustomUnit(e.target.value)}
                />
              </div>
              <div className="sbs-custom-field">
                <label className="sbs-custom-label">{t.custom_topic}</label>
                <input
                  className="sbs-custom-input"
                  placeholder={t.custom_ph}
                  value={customTopic}
                  onChange={e => setCustomTopic(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
          </div>
        )}

        <div style={{ height: 120 }} />
      </main>

      <div className="sbs-sticky">
        {canStart && (
          <div className="sbs-start-info">
            <span className="sbs-start-info-emoji">{startEmoji}</span>
            <div className="sbs-start-info-text">
              <span className="sbs-start-info-subject">{startSubjectLabel}</span>
              {startUnitLabel && (
                <span className="sbs-start-info-meta">
                  {startUnitLabel}
                  {startTopicLabel && ` · ${startTopicLabel}`}
                </span>
              )}
            </div>
          </div>
        )}
        <button
          className={`sbs-start-btn${canStart ? ' sbs-start-active' : ''}`}
          disabled={!canStart}
          onClick={handleStart}
        >
          {starting ? t.starting : t.start}
        </button>
      </div>
    </div>
  );
}

const CSS = `
/* ── Layout ── */
.sbs-root { display:flex; flex-direction:column; min-height:100vh; background:linear-gradient(160deg,#FFF9F0 0%,#F0FDF9 50%,#F5F0FF 100%); font-family:'Nunito',sans-serif; position:relative; }
.sbs-header { background:linear-gradient(135deg,#059669,#10B981); padding:18px 32px; display:flex; align-items:center; justify-content:space-between; box-shadow:0 4px 20px rgba(5,150,105,.25); }
.sbs-header-title { font-family:'Sora',sans-serif; font-size:20px; font-weight:700; color:#fff; }
.sbs-main { flex:1; max-width:960px; margin:0 auto; width:100%; padding:32px 24px 120px; position:relative; z-index:10; }

/* ── Incomplete banner ── */
.sbs-banner { background:#FEF9EE; border:2px solid #FDE68A; border-radius:20px; padding:14px 16px; margin-bottom:20px; display:flex; flex-direction:column; gap:10px; box-shadow:0 5px 0 #FDE68A, 0 8px 20px rgba(245,158,11,.12); }
.sbs-banner-title { font-size:14px; font-weight:700; color:#92400E; display:flex; align-items:center; gap:8px; }
.sbs-banner-actions { display:flex; gap:10px; }
.sbs-banner-cont { background:linear-gradient(135deg,#D97706,#F59E0B); color:#fff; border:none; border-radius:10px; padding:8px 18px; font-size:13px; font-weight:700; cursor:pointer; font-family:'Nunito',sans-serif; }
@keyframes sbs-banner-pulse { 0%,100%{box-shadow:0 0 0 0 rgba(245,158,11,.15)} 50%{box-shadow:0 0 0 8px rgba(245,158,11,0)} }

/* ── Sticky bottom bar ── */
.sbs-sticky-bar { position:fixed; bottom:0; left:0; right:0; background:rgba(255,255,255,.95); backdrop-filter:blur(12px); border-top:1px solid #E5E7EB; padding:12px 24px; display:flex; justify-content:center; z-index:100; }
.sbs-start-btn { background:#D1FAE5; color:#065F46; border:2px solid #6EE7B7; border-radius:18px; padding:14px 40px; font-size:15px; font-weight:900; cursor:pointer; font-family:'Sora',sans-serif; box-shadow:0 5px 0 #6EE7B7, 0 8px 16px rgba(5,150,105,.12); transition:transform .08s, box-shadow .08s; }
.sbs-start-btn:hover { transform:translateY(-3px); box-shadow:0 5px 0 #6EE7B7, 0 14px 24px rgba(5,150,105,.2); }
.sbs-start-btn:active { transform:translateY(4px); box-shadow:0 1px 0 #6EE7B7; }
.sbs-start-btn:disabled { opacity:.5; cursor:not-allowed; transform:none; }

/* ── Loading / Error ── */
.sbs-spinner { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:16px; padding:60px; color:#6B7280; font-size:14px; }
.sbs-spin { width:36px; height:36px; border:3px solid #E5E7EB; border-top-color:#059669; border-radius:50%; animation:spin .8s linear infinite; }
@keyframes spin { to{transform:rotate(360deg)} }
.sbs-error { text-align:center; padding:60px; color:#DC2626; }
.sbs-empty { text-align:center; padding:60px; color:#6B7280; font-size:15px; }

/* ── Custom mode ── */
.sbs-custom-full { margin-top:24px; background:#fff; border:2px solid #E5E7EB; border-radius:24px; padding:24px; box-shadow:0 5px 0 #E5E7EB, 0 10px 28px rgba(0,0,0,.07); }
.sbs-custom-close { position:absolute; top:14px; inset-inline-end:16px; background:#F9FAFB; border:1px solid #E5E7EB; color:#6B7280; font-size:14px; width:28px; height:28px; border-radius:50%; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; font-family:inherit; }
.sbs-custom-close:hover { background:#FEF3C7; border-color:#F59E0B; color:#D97706; }
.sbs-custom-full-header { display:flex; align-items:flex-start; gap:10px; margin-bottom:18px; padding-inline-end:36px; }
.sbs-custom-full-emoji { font-size:24px; flex-shrink:0; }
.sbs-custom-full-header-text { display:flex; flex-direction:column; gap:2px; }
.sbs-custom-full-subject { font-size:14px; font-weight:700; color:#059669; }
.sbs-custom-subtitle { font-size:12px; color:#6B7280; }
.sbs-custom-full-fields { display:flex; flex-direction:column; gap:14px; }
.sbs-custom-field { display:flex; flex-direction:column; gap:5px; }
.sbs-custom-label { font-size:13px; font-weight:600; color:#059669; }
.sbs-custom-input { border:2px solid #E5E7EB; border-radius:14px; padding:12px 16px; font-size:14px; color:#111827; font-family:inherit; outline:none; transition:border-color .2s, box-shadow .2s; direction:inherit; background:white; width:100%; }
.sbs-custom-input:focus { border-color:#059669; box-shadow:0 0 0 4px rgba(5,150,105,.1); }
.sbs-custom-input-locked { background:#F9FAFB; color:#6B7280; cursor:default; border-color:#E5E7EB; }
.sbs-custom-input-locked:focus { border-color:#E5E7EB; box-shadow:none; }

/* ── Sticky bar (existing sbs-sticky kept for compatibility) ── */
.sbs-sticky { position:fixed; bottom:0; left:0; right:0; background:linear-gradient(to top,#F0FDF4 70%,transparent); padding:12px 20px 22px; display:flex; flex-direction:column; align-items:center; gap:8px; z-index:200; }
.sbs-start-info { display:flex; align-items:center; gap:10px; background:white; border:2px solid #D1FAE5; border-radius:18px; padding:8px 16px; width:100%; max-width:400px; box-shadow:0 3px 0 #D1FAE5, 0 6px 14px rgba(5,150,105,.08); }
.sbs-start-info-emoji { font-size:20px; flex-shrink:0; }
.sbs-start-info-text { display:flex; flex-direction:column; gap:1px; min-width:0; }
.sbs-start-info-subject { font-size:13px; font-weight:700; color:#059669; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.sbs-start-info-meta { font-size:11px; color:#6B7280; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.sbs-back { background:rgba(255,255,255,.2); border:2px solid rgba(255,255,255,.3); color:white; font-size:14px; padding:6px 14px; border-radius:20px; cursor:pointer; white-space:nowrap; transition:background .2s; box-shadow:0 2px 0 rgba(0,0,0,.1); }
.sbs-back:hover { background:rgba(255,255,255,.35); }
.sbs-title { flex:1; text-align:center; color:white; font-size:18px; font-weight:700; }
.sbs-header-spacer { width:72px; }
.sbs-center { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:60px 20px; gap:16px; }
.sbs-hint { color:#6B7280; font-size:15px; }
.sbs-retry-btn { background:#059669; color:white; border:none; padding:10px 24px; border-radius:20px; cursor:pointer; font-size:14px; font-weight:600; }

@media (max-width: 767px) {
  .sbs-main { padding: 12px 14px 120px; }
  .sbs-subj-grid { grid-template-columns: repeat(2,1fr) !important; }
}
@media (min-width: 768px) and (max-width: 1023px) {
  .sbs-subj-grid { grid-template-columns: repeat(3,1fr) !important; }
}
@media (min-width: 1024px) {
  .sbs-subj-grid { grid-template-columns: repeat(4,1fr) !important; }
}

`;
